import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Typography,
  Alert,
  Button,
} from '@mui/material'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { UUID } from 'crypto'
import ErrorMessage from './ErrorMessage'
import Loading from './Loading'
import {
  OpportunitySuggetionFormData,
  submitOppSuggestionRequest,
} from '@/api/feedbackAPI'

// Props interface for the component
interface HelpDialogProps {
  open: boolean
  onClose: () => void
  fundingNeedTitle: string
}

export default function SubmitOppSuggestion({
  open,
  onClose,
  fundingNeedTitle,
}: HelpDialogProps) {
  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const {
    orgData,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])
  const [organisation, setOrganisation] = useState(orgData?.[0])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  useEffect(() => {
    if (user && orgData && orgData.length > 0) {
      if (!user.selected_org_uuid) {
        setOrganisation(orgData[0])
        return
      } else {
        const selectedOrg = orgData.find(
          (org) => org.uuid === user.selected_org_uuid
        )
        if (selectedOrg) {
          setOrganisation(selectedOrg || orgData[0])
        }
      }
    }
  }, [orgData, organisation, user])

  // State for form data
  const [formData, setFormData] = useState<OpportunitySuggetionFormData>({
    organisationName: organisation?.name || '',
    orgUuid: effectiveOrgUuid,
    userName: user?.first_name + ' ' + user?.last_name,
    email: user?.email,
    fundingNeedTitle: fundingNeedTitle,
    description: '',
  })

  // State for form validation
  const [errors, setErrors] = useState({
    description: false,
  })

  // Handle text field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error for this field if it was previously marked as error
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: false,
      })
    }
  }
  // Handle form submission
  async function handleSubmit() {
    if (!effectiveOrgUuid || !token) {
      return
    }

    // Validate form
    const newErrors = {
      description: formData.description.trim() === '',
    }

    setErrors(newErrors)

    if (!Object.values(newErrors).includes(true)) {
      setSubmitError(null)
      setSubmitting(true)
      setSubmitted(false)

      try {
        const result = await submitOppSuggestionRequest({ ...formData }, token)

        // Reset form
        setFormData({
          ...formData,
          description: '',
        })

        if (result) {
          setSubmitted(true)
          setSubmitting(false)
        } else {
          setSubmitting(false)
          setSubmitError(
            'Failed to submit feedback - please email us directly or try again later'
          )
        }
      } catch {
        setSubmitting(false)
        setSubmitError(
          'Failed to submit feedback - please email us directly or try again later'
        )
      }
    }
  }

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  if (userLoading || orgsLoading) {
    return <Loading />
  }

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {!user ||
        !token ||
        ((!organisation || !organisation.uuid) && <ErrorMessage />)}
      {organisation ? (
        <>
          <DialogTitle>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
              }}
            >
              Have we missed something?
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2, textAlign: 'center' }}>
              Please let us know if there are funding opportunities you think we
              should know about
            </DialogContentText>

            <Box
              component="form"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: 600,
                margin: '0 auto',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/* Description field */}
              <TextField
                name="description"
                label="Funding opportunity information"
                multiline
                minRows={4}
                maxRows={10}
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="dense"
                required
                error={errors.description}
                helperText={
                  errors.description ? 'Please describe your issue' : ''
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#757575',
                  },
                }}
              />

              <Button
                onClick={handleSubmit}
                disabled={submitting || submitted}
                variant="contained"
                disableRipple
                sx={{
                  borderRadius: 3,
                  bgcolor: '#1C3045',
                  color: 'white',
                  m: 0.5,
                  width: '50%',
                  fontSize: '22px',
                  textTransform: 'none',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>

              {submitError && <Alert severity="error">{submitError}</Alert>}
              {submitted && !submitError && (
                <Alert severity="success">Thank you for you suggestion!</Alert>
              )}
            </Box>
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  )
}
